import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Shield, Smartphone, AlertCircle, CheckCircle, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TwoFactorAuthModal({ onClose, user }) {
  const [step, setStep] = useState(user?.two_factor_enabled ? 'enabled' : 'init');
  const [loading, setLoading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');

  const enableTwoFactor = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/auth/2fa/enable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrData(response.data);
      setStep('scan');
    } catch (error) {
      toast.error('Failed to enable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const verifyTwoFactor = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/2fa/verify`,
        { token: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('2FA enabled successfully!');
      setStep('enabled');
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const disableTwoFactor = async () => {
    if (!window.confirm('Are you sure you want to disable 2FA?')) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/2fa/disable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('2FA disabled');
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error('Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(qrData.secret);
    toast.success('Secret key copied to clipboard');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="2fa-modal">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-xl">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <h2 className="font-manrope font-bold text-2xl text-text-primary">Two-Factor Authentication</h2>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary">
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {step === 'init' && (
            <div className="text-center">
              <div className="bg-primary/10 p-6 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <Smartphone className="w-10 h-10 text-primary" />
              </div>
              <h3 className="font-semibold text-xl text-text-primary mb-2">Secure Your Account</h3>
              <p className="text-text-secondary mb-6">
                Add an extra layer of security by enabling two-factor authentication. You'll need an authenticator app like Google Authenticator or Authy.
              </p>
              <button
                onClick={enableTwoFactor}
                disabled={loading}
                className="btn-primary w-full"
                data-testid="btn-enable-2fa"
              >
                {loading ? 'Setting up...' : 'Enable 2FA'}
              </button>
            </div>
          )}

          {step === 'scan' && qrData && (
            <div>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <p className="text-sm text-text-secondary mb-3">Scan this QR code with your authenticator app:</p>
                <div className="bg-white p-4 rounded-lg flex justify-center">
                  <img src={qrData.qr_code_url} alt="QR Code" className="w-48 h-48" />
                </div>
                <div className="mt-4">
                  <p className="text-xs text-text-muted mb-2">Or enter this code manually:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded-lg text-sm font-mono border border-gray-200">
                      {qrData.secret}
                    </code>
                    <button
                      onClick={copySecret}
                      className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                      title="Copy secret"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Enter 6-digit verification code
                </label>
                <input
                  type="text"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 text-center text-2xl font-mono tracking-widest focus:border-primary focus:outline-none"
                  placeholder="000000"
                  data-testid="input-verification-code"
                />
              </div>

              <button
                onClick={verifyTwoFactor}
                disabled={loading || verificationCode.length !== 6}
                className="btn-primary w-full"
                data-testid="btn-verify-2fa"
              >
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>
          )}

          {step === 'enabled' && (
            <div className="text-center">
              <div className="bg-green-100 p-6 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="font-semibold text-xl text-text-primary mb-2">2FA is Active</h3>
              <p className="text-text-secondary mb-6">
                Your account is protected with two-factor authentication. You'll need your authenticator app to sign in.
              </p>
              <button
                onClick={disableTwoFactor}
                disabled={loading}
                className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
                data-testid="btn-disable-2fa"
              >
                {loading ? 'Disabling...' : 'Disable 2FA'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}